import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASONS = [
  { value: 'truck_breakdown',    label: 'Truck breakdown'    },
  { value: 'personal_emergency', label: 'Personal emergency' },
  { value: 'rate_dispute',       label: 'Rate dispute'       },
  { value: 'other',              label: 'Other'              },
];

const CannotCoverButton = ({ loadId, driverEmail, onCovered }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error('Please pick a reason');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/loads/${loadId}/cannot-cover`, {
        driver_email: driverEmail, reason, detail,
      });
      if (r.data.needs_manual_intervention) {
        toast.warning('No backup carrier available — flagged for manual intervention');
      } else if (r.data.redispatched) {
        toast.success(`Backup carrier ${r.data.to_slot.replace('_', ' ')} dispatched`);
      } else {
        toast('Cannot cover logged');
      }
      if (r.data.auto_confirm_disabled) {
        toast.warning('Auto-Confirm disabled — suspension warning sent', { duration: 8000 });
      }
      setOpen(false);
      onCovered?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`cannot-cover-btn-${loadId}`}
      >
        <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Cannot Cover This Load
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cannot-cover-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" /> Report Cannot Cover
            </DialogTitle>
            <DialogDescription>
              Per the Carrier Commitment Policy, this action will log a Trust Score penalty
              and dispatch a backup carrier from our pre-vetted queue. A 3rd event in 30
              days automatically disables Auto-Confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="text-xs font-medium">Reason</div>
              <div className="grid grid-cols-2 gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => setReason(r.value)}
                    className={`px-3 py-2 text-sm rounded-md border text-left transition
                      ${reason === r.value
                        ? 'border-red-500 bg-red-500/10 text-red-300'
                        : 'border-zinc-700 hover:border-zinc-500'}`}
                    data-testid={`reason-${r.value}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs font-medium">Additional detail (optional)</div>
              <Textarea
                value={detail} onChange={(e) => setDetail(e.target.value)}
                rows={3} placeholder="Any context for the founder reviewing this event"
                data-testid="cannot-cover-detail"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submit}
                    disabled={!reason || submitting}
                    data-testid="cannot-cover-confirm-btn">
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Logging…</>
                : 'Confirm Cannot Cover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CannotCoverButton;
